import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { api, getToken, setToken, removeToken, getUser, setUser, removeUser } from '../utils/api';
import { setUserContext, clearUserContext } from '../utils/sentry';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(() => getUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    // 始终用 /me 刷新（含 isFounder），避免本地缓存缺字段导致入口误显/误藏
    api.auth.me()
      .then((u) => {
        setUserState(u);
        setUser(u);
        setUserContext(u);
      })
      .catch((err) => {
        // 仅「真未登录/凭证失效(401)」才清会话。
        // 限流(429)、超时、网络抖动、发版重启(5xx) 一律保留本地会话 ——
        // 否则一次限流就把全家人静默踢出 App（2026-09-29 走查实锤的 P0）。
        if (err?.status === 401) {
          removeToken();
          removeUser();
          setUserState(null);
          clearUserContext();
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (token, user) => {
    // 只接受 (token, user)。
    //
    // 原先这里还有一个「旧流程兼容」分支会去调 api.auth.login(phone, nickname)，
    // 手机号登录退役后那个方法已经不存在了 —— 留着它只会在某个漏改的调用点上
    // 抛一个"api.auth.login is not a function"，而不是在编译期就被发现。
    setToken(token);
    setUser(user);
    setUserState(user);
    setUserContext(user);
    return { token, user };
  }, []);

  const logout = useCallback(() => {
    removeToken();
    removeUser();
    setUserState(null);
    clearUserContext();
  }, []);

  const updateUser = useCallback(async (data) => {
    await api.auth.updateMe(data);
    const updated = await api.auth.me();
    setUser(updated);
    setUserState(updated);
    setUserContext(updated);
    return updated;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
